import { useCallback, useMemo } from "react";
import {
  useInfiniteQuery,
  useQueryClient,
  type FetchNextPageOptions,
  type InfiniteData,
} from "@tanstack/react-query";
import type { AdminMediaResponse, MediaListQuery, MediaListResponse } from "@ckgo2/shared/types";
import { MediaListQuerySchema } from "@ckgo2/shared";
import { api, type ApiResponse } from "@/api/client";
import { MEDIA_LIST_KEY } from "@/hooks/useMediaSources";

/**
 * Seznam knihovny fotek (stránka Média a okno výběru fotek; jediný čtenář seznamu v adminu):
 * filtry, dotaz `GET /api/admin/upload` po stránkách a čisté pomocníky nad stránkami (vize
 * podnety-tymu-2026-10, F7.5, F7.11). Stránka Média drží filtry v adrese, okno v místním stavu.
 *
 * Filtry žijí v adrese pod týmž jménem jako parametr API a platí pro ně sdílené
 * `MediaListQuerySchema`; jedinou výjimkou je koš, `trash=1` v adrese (API
 * `includeTrashed=true`). Parametr `media` (otevřený detail, `MEDIA_DETAIL_PARAM`) filtr
 * není: nepočítá se do „Zrušit filtry“ a `setMediaFilter` ho nechává.
 */

/** Textové filtry: v adrese i v API pod týmž jménem, hodnota podle `MediaListQuerySchema`. */
const STRING_FILTERS = [
  "q",
  "country",
  "source",
  "orientation",
  "usage",
  "folder",
  "folderId",
] as const satisfies readonly (keyof MediaListQuery)[];

export type MediaLibraryFilters = Pick<MediaListQuery, (typeof STRING_FILTERS)[number]> & {
  starred?: true;
  trash?: true;
  /** Pořadí výběru (`MediaListQuerySchema.sort`): okno výběru fotek posílá vždy `"starred"`,
   *  stránka Média nikdy. Pořadí, ne filtr: do dotazu ho zapisuje a z počtu filtrů vyjímá
   *  `mediaListSearch` / `activeMediaFilterCount` (řez 05, část K2). */
  sort?: "starred";
};
export type MediaFilterKey = keyof MediaLibraryFilters;

type Pages = InfiniteData<ApiResponse<MediaListResponse>, string>;

/** Velikost stránky seznamu (dlaždic na jedno donačtení). */
const PAGE_SIZE = 60;

/**
 * Každé pole přes tvar `MediaListQuerySchema`; neplatné nebo prázdné pole zahodí
 * (orientation=foo, folder=neexistuje, starred=0, q delší než API přijme).
 */
export function parseMediaFilters(params: URLSearchParams): MediaLibraryFilters {
  const f: MediaLibraryFilters = {};
  for (const key of STRING_FILTERS) {
    const parsed = MediaListQuerySchema.shape[key].safeParse(params.get(key) ?? undefined);
    if (parsed.success && parsed.data) Object.assign(f, { [key]: parsed.data });
  }
  if (MediaListQuerySchema.shape.starred.safeParse(params.get("starred") ?? undefined).data) {
    f.starred = true;
  }
  if (params.get("trash") === "1") f.trash = true;
  return f;
}

/** Nová instance; `value` null/"" klíč smaže; `media` i ostatní klíče zachová. */
export function setMediaFilter(
  params: URLSearchParams,
  key: MediaFilterKey,
  value: string | null,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (value === null || value === "") next.delete(key);
  else next.set(key, value);
  return next;
}

/** Parametr adresy s ID fotky otevřené v detailu (`/media?media=<id>`); filtr to není. */
export const MEDIA_DETAIL_PARAM = "media";

/** Nová instance s otevřeným detailem fotky `id` (null detail zavře); filtry zachová. */
export function setMediaDetail(params: URLSearchParams, id: string | null): URLSearchParams {
  const next = new URLSearchParams(params);
  if (id) next.set(MEDIA_DETAIL_PARAM, id);
  else next.delete(MEDIA_DETAIL_PARAM);
  return next;
}

/** Odkaz na stránku Média s otevřeným detailem fotky (bez filtrů, tedy živé fotky). */
export function mediaDetailHref(id: string): string {
  return `/media?${MEDIA_DETAIL_PARAM}=${encodeURIComponent(id)}`;
}

/** Prázdné parametry (zruší filtry i `media`). */
export function clearMediaFilters(): URLSearchParams {
  return new URLSearchParams();
}

/**
 * Počet aktivních filtrů včetně `q`, `folderId` a `trash` (pro „Zrušit filtry“). Pořadí
 * `sort` filtr není: okno výběru ho posílá vždy a bez filtrů nesmí svítit „Zrušit filtry (1)“.
 */
export function activeMediaFilterCount(f: MediaLibraryFilters): number {
  return Object.entries(f).filter(([k, v]) => k !== "sort" && v !== undefined).length;
}

/**
 * Dotaz API: `limit=60`, `cursor` jen neprázdný, `trash` → `includeTrashed=true`, `sort`
 * 1:1 (pořadí okna výběru; `parseMediaFilters` ho z adresy stránky Média nečte).
 */
export function mediaListSearch(f: MediaLibraryFilters, cursor: string): URLSearchParams {
  const s = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (cursor) s.set("cursor", cursor);
  for (const key of STRING_FILTERS) {
    const value = f[key];
    if (value) s.set(key, value);
  }
  if (f.starred) s.set("starred", "1");
  if (f.trash) s.set("includeTrashed", "true");
  if (f.sort) s.set("sort", f.sort);
  return s;
}

/**
 * Načtené položky v pořadí stránek, každé ID jednou. Kurzor je offset: když mezi dvěma
 * stránkami přibude nahoře fotka (nahrání v jiném okně), další stránka začne poslední
 * fotkou předchozí. Bez odduplikování by mřížka nesla dvě dlaždice s týmž klíčem a počet
 * dlaždic by nesouhlasil s „Vybrat vše načtené“.
 */
export function mediaPagesItems(data: Pages | undefined): AdminMediaResponse[] {
  const seen = new Set<string>();
  const out: AdminMediaResponse[] = [];
  for (const p of data?.pages ?? []) {
    for (const m of p.data?.data ?? []) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      out.push(m);
    }
  }
  return out;
}

/** `total` poslední načtené stránky (nejčerstvější), bez dat null. */
export function mediaPagesTotal(data: Pages | undefined): number | null {
  const last = data?.pages[data.pages.length - 1];
  return last?.data ? last.data.total : null;
}

/** Nahradí položku se shodným `id` ve všech stránkách (po ★ v prohlížeči); bez ní `data`. */
export function patchMediaPages(data: Pages, item: AdminMediaResponse): Pages {
  if (!data.pages.some((p) => p.data?.data.some((m) => m.id === item.id))) return data;
  return {
    ...data,
    pages: data.pages.map((p) =>
      p.data?.data.some((m) => m.id === item.id)
        ? { ...p, data: { ...p.data, data: p.data.data.map((m) => (m.id === item.id ? item : m)) } }
        : p,
    ),
  };
}

/**
 * Odebere položku ze stránek, jen když tam byla: sníží `total` každé stránky o 1
 * a `nextCursor` poslední stránky (offset jako řetězec, když není null) o 1, aby další
 * stránka po smazání žádnou fotku nepřeskočila (Kontrakt bod 6, `onDeleted`).
 */
export function removeFromMediaPages(data: Pages, id: string): Pages {
  if (!data.pages.some((p) => p.data?.data.some((m) => m.id === id))) return data;
  const lastIndex = data.pages.length - 1;
  return {
    ...data,
    pages: data.pages.map((p, i) => {
      if (!p.data) return p;
      let nextCursor = p.data.nextCursor;
      if (i === lastIndex && nextCursor !== null) {
        const offset = Number(nextCursor);
        if (Number.isFinite(offset) && offset > 0) nextCursor = String(offset - 1);
      }
      return {
        ...p,
        data: {
          ...p.data,
          data: p.data.data.filter((m) => m.id !== id),
          total: Math.max(0, p.data.total - 1),
          nextCursor,
        },
      };
    }),
  };
}

export type MediaGridState = "loading" | "error" | "empty" | "items";

/**
 * Jediný výpočet stavu mřížky (čte ho `MediaPage` pro mřížku i pro `showClear` lišty):
 * `isLoading` → loading; `isError && itemsLength === 0` → error; `itemsLength === 0` → empty;
 * jinak items s `nextPageError = isFetchNextPageError` (chyba obnovení na pozadí s položkami
 * není chyba další stránky).
 */
export function mediaGridState(s: {
  isLoading: boolean;
  isError: boolean;
  isFetchNextPageError: boolean;
  itemsLength: number;
}): { state: MediaGridState; nextPageError: boolean } {
  if (s.isLoading) return { state: "loading", nextPageError: false };
  if (s.isError && s.itemsLength === 0) return { state: "error", nextPageError: false };
  if (s.itemsLength === 0) return { state: "empty", nextPageError: false };
  return { state: "items", nextPageError: s.isFetchNextPageError };
}

/**
 * Donačíst další stránku? Ne, dokud seznam cokoli načítá: `fetchNextPage` by zrušil
 * probíhající obnovu všech stránek (po zneplatnění) a další stránku přidal ke starým, takže
 * by přesunuté fotky v mřížce zůstaly a posunutý offset jiné přeskočil. Po chybě další
 * stránky také ne: `hasNextPage` zůstane pravdivé (počítá se z poslední úspěšné stránky)
 * a nový pozorovatel nad viditelným sentinelem by jinak opakoval požadavek dokola.
 * Donačítání po chybě obnoví jen „Zkusit znovu“.
 */
export function shouldLoadMore(s: {
  isIntersecting: boolean;
  hasNextPage: boolean;
  /** Načítá se cokoli (první stránka, obnova na pozadí i další stránka). */
  isFetching: boolean;
  isFetchNextPageError: boolean;
}): boolean {
  return s.isIntersecting && s.hasNextPage && !s.isFetching && !s.isFetchNextPageError;
}

export interface MediaLibrary {
  items: AdminMediaResponse[];
  total: number | null;
  hasNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  /** `throwOnError: true` = slib chybu donačtení odmítne (prohlížeč ji ukáže). */
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<unknown>;
  refetch: () => Promise<unknown>;
  patchItem: (item: AdminMediaResponse) => void;
  removeItem: (id: string) => void;
}

/**
 * Seznam fotek pro filtry. Bez `placeholderData`: po změně filtru se ukáže kostra, ne stará
 * data se starým počtem (E2E čte `[data-media-total]` hned po změně filtru).
 *
 * `refetchOnMount: "always"` předává okno výběru fotek: seznam z keše (globální `staleTime`
 * 5 min) by v okně otevřeném znovu neukázal fotky nahrané mezitím jinudy ani čerstvé
 * „použito N×“. Stránka Média volbu nepředává.
 */
export function useMediaLibrary(
  filters: MediaLibraryFilters,
  options?: { refetchOnMount?: "always" },
): MediaLibrary {
  const queryClient = useQueryClient();
  const query = useInfiniteQuery<
    ApiResponse<MediaListResponse>,
    Error,
    Pages,
    readonly unknown[],
    string
  >({
    queryKey: [...MEDIA_LIST_KEY, filters],
    queryFn: ({ pageParam }) =>
      api.get<ApiResponse<MediaListResponse>>(
        `/api/admin/upload?${mediaListSearch(filters, pageParam)}`,
      ),
    initialPageParam: "",
    getNextPageParam: (p) => p.data?.nextCursor ?? undefined,
    ...(options?.refetchOnMount ? { refetchOnMount: options.refetchOnMount } : {}),
  });

  // Zápis do VŠECH seznamů v keši (každá varianta filtrů): obě čisté funkce seznam bez
  // dotčené fotky vrátí beze změny, takže ★ z prohlížeče platí i v jiných filtrech v keši
  // a zpětná volání na filtrech nezávisí.
  const patchItem = useCallback(
    (item: AdminMediaResponse) => {
      queryClient.setQueriesData<Pages>({ queryKey: MEDIA_LIST_KEY }, (old) =>
        old ? patchMediaPages(old, item) : old,
      );
    },
    [queryClient],
  );
  const removeItem = useCallback(
    (id: string) => {
      queryClient.setQueriesData<Pages>({ queryKey: MEDIA_LIST_KEY }, (old) =>
        old ? removeFromMediaPages(old, id) : old,
      );
    },
    [queryClient],
  );

  const items = useMemo(() => mediaPagesItems(query.data), [query.data]);

  return {
    items,
    total: mediaPagesTotal(query.data),
    hasNextPage: query.hasNextPage,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetchNextPageError: query.isFetchNextPageError,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    patchItem,
    removeItem,
  };
}
